import { useMutation, useQueryClient } from '@tanstack/react-query'
import { API_BASE_URL, invoiceFetch as apiFetch } from '@/api/client'
import { useAuth } from '@/hooks/use-auth'
import { invoiceKeys } from '@/features/invoices/keys'
import { USE_MOCK_AP_INVOICES } from '../mock'
import type { ApInvoicePaymentInput, ApInvoicePaymentReplacementInput } from '../types'
import type {
  ApInvoicePayment,
  ApInvoicePaymentReceipt,
  ApInvoicePaymentReplacementResult,
} from '../view-models'

export function useRecordInvoicePayment(uuid: string) {
  const { getAccessToken } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (body: ApInvoicePaymentInput) => {
      if (USE_MOCK_AP_INVOICES) {
        return { id: 9881, created_at: new Date().toISOString(), ...body }
      }
      const token = await getAccessToken()
      if (!token) throw new Error('Failed to acquire access token')
      return apiFetch<ApInvoicePayment>(token, `/dashboard/ap-invoices/${uuid}/payments`, {
        method: 'POST',
        json: body,
      })
    },
    // A refused payment often means someone (or the email AI) just paid it: show what is there now.
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all })
      queryClient.invalidateQueries({
        queryKey: invoiceKeys.detail(uuid),
      })
    },
  })
}

export function useReplaceInvoicePayment(uuid: string) {
  const { getAccessToken } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      paymentId,
      body,
    }: {
      paymentId: number
      body: ApInvoicePaymentReplacementInput
    }) => {
      const token = await getAccessToken()
      if (!token) throw new Error('Failed to acquire access token')
      return apiFetch<ApInvoicePaymentReplacementResult>(
        token,
        `/dashboard/ap-invoices/${uuid}/payments/${paymentId}/replace`,
        { method: 'POST', json: body },
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all })
      queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(uuid) })
    },
  })
}

export function useUploadInvoicePaymentReceipt(uuid: string) {
  const { getAccessToken } = useAuth()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      paymentId,
      file,
      source,
    }: {
      paymentId: number
      file: File
      source: 'manual_upload' | 'clipboard_paste'
    }) => {
      const token = await getAccessToken()
      if (!token) throw new Error('Failed to acquire access token')
      const formData = new FormData()
      formData.append('file', file)
      formData.append('source', source)
      return apiFetch<ApInvoicePaymentReceipt>(
        token,
        `/dashboard/ap-invoices/${uuid}/payments/${paymentId}/receipt-attachments`,
        { method: 'POST', body: formData },
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(uuid) })
    },
  })
}

export async function downloadInvoicePaymentReceipt(
  token: string,
  invoiceUuid: string,
  paymentId: number,
  attachmentId: number,
  download = false,
): Promise<Blob> {
  const response = await fetch(
    `${API_BASE_URL}/dashboard/ap-invoices/${invoiceUuid}/payments/${paymentId}/receipt-attachments/${attachmentId}/download?download=${download}`,
    { headers: { Authorization: 'Bearer ' + token } },
  )
  if (!response.ok) throw new Error(`Payment receipt download failed with ${response.status}`)
  return response.blob()
}
